import { memo, useState } from 'react'

import { CopyAll, Folder, KeyboardArrowDown, SettingsInputHdmi } from '@mui/icons-material'
import { IconButton, ListItemIcon, ListItemText, Menu, MenuItem, Paper, Typography } from '@mui/material'
import { AvcInstanceAvioDirectory, AvcInstanceAvioNdi } from '@nativewaves/nw-cloud-sdk/content'
import { useSnackbar } from 'notistack'
import CopyToClipboard from 'react-copy-to-clipboard'
import { useTranslation } from 'react-i18next'

import { CodeInline } from '@shared/components/MaterialUIEnhancements/CodeInline'

type AVIOPanel =
  | {
      ndi: AvcInstanceAvioNdi
      directory?: never
    }
  | {
      ndi?: never
      directory: AvcInstanceAvioDirectory
    }

const AVIOPanelRaw = ({ ndi, directory }: AVIOPanel) => {
  const { t } = useTranslation(['common', 'domain'])
  const { enqueueSnackbar } = useSnackbar()

  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement>()

  const derivedId = directory?.id || ndi!.url
  const derivedName = directory?.name || ndi!.name

  return (
    <Paper
      sx={{
        p: 4,
        pb: 3,
        minWidth: 200,
        position: 'relative',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
      }}
      key={derivedId}
      elevation={0}
    >
      <IconButton
        sx={{ position: 'absolute', top: 4, right: 4 }}
        size="small"
        onClick={(e) => setAnchorEl(e.currentTarget)}
      >
        <KeyboardArrowDown fontSize="inherit" />
      </IconButton>
      {ndi && <SettingsInputHdmi sx={{ mb: 2 }} fontSize="large" />}
      {directory && <Folder sx={{ mb: 2 }} fontSize="large" />}

      <Typography variant="heading-sm">{derivedName}</Typography>

      {ndi && <CodeInline sx={{ mt: 1 }}>{ndi.url}</CodeInline>}
      {directory && <CodeInline>{directory.path}</CodeInline>}
      <Menu
        open={!!anchorEl}
        anchorEl={anchorEl}
        anchorReference="anchorEl"
        anchorOrigin={{
          horizontal: 'center',
          vertical: 'bottom',
        }}
        transformOrigin={{
          horizontal: 'center',
          vertical: 'top',
        }}
        onClose={() => setAnchorEl(undefined)}
        MenuListProps={{ disablePadding: true }}
      >
        <CopyToClipboard
          text={derivedId}
          onCopy={() => {
            enqueueSnackbar(t('copiedValueToClipboard', { value: 'ID' }), { variant: 'info' })
            setAnchorEl(undefined)
          }}
        >
          <MenuItem>
            <ListItemIcon>
              <CopyAll fontSize="inherit" />
            </ListItemIcon>
            <ListItemText primary={t('domain:ContentProcessing.Instance.copyId')} />
          </MenuItem>
        </CopyToClipboard>
      </Menu>
    </Paper>
  )
}

export const AVIOPanel = memo(AVIOPanelRaw)
